// Menu categories hooks
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { queryKeys } from '@/lib/query-client'
import type { MenuCategory } from '@/types/entities'
import { applyLanguageFallback } from '@/lib/language-fallback'
import { isAssistantLoggedIn } from '@/lib/permissions'

/**
 * Fetch menu categories for a restaurant
 */
export function useMenuCategories(restaurantId: string | null) {
  return useQuery({
    queryKey: ['menuCategories', restaurantId],
    queryFn: async (): Promise<MenuCategory[]> => {
      if (!restaurantId) {
        return []
      }

      // Use RPC function to bypass RLS (allows staff to view categories)
      const { data, error } = await supabase
        .rpc('get_restaurant_menu_categories', { p_restaurant_id: restaurantId })

      if (error) {
        throw error
      }

      return (data || []).map((item: any) => ({
        id: item.id,
        restaurantId: item.restaurant_id,
        nameAr: item.name_ar,
        nameFr: item.name_fr,
        displayOrder: item.display_order || 0,
        quickNotes: item.quick_notes || [],
        createdAt: item.created_at || '',
        updatedAt: item.updated_at || '',
      }))
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!restaurantId,
  })
}

/**
 * Create menu category mutation
 * Supports both admin users and assistants with appropriate permissions
 */
export function useCreateMenuCategory(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { nameAr?: string; nameFr?: string; displayOrder?: number; quickNotes?: string[] }) => {
      // Apply fallback: if one language is missing, use the other
      const withFallback = applyLanguageFallback({
        nameAr: data.nameAr || null,
        nameFr: data.nameFr || null,
      })

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {

        // RPC function added by migration 20251211000003
        const { data: categoryId, error } = await (supabase.rpc as any)('assistant_create_menu_category', {
          
          p_restaurant_id: restaurantId,
          p_name_ar: withFallback.nameAr || '',
          p_name_fr: withFallback.nameFr || '',
          p_display_order: data.displayOrder || 0,
          p_quick_notes: data.quickNotes || [],
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied')
          }
          throw error
        }

        return {
          id: categoryId,
          restaurantId,
          nameAr: withFallback.nameAr || '',
          nameFr: withFallback.nameFr || '',
          displayOrder: data.displayOrder || 0,
          quickNotes: data.quickNotes || [],
          createdAt: '',
          updatedAt: '',
        }
      }

      // Admin user - use direct query
      const { data: result, error } = await supabase
        .from('menu_categories')
        .insert({
          restaurant_id: restaurantId,
          name_ar: withFallback.nameAr,
          name_fr: withFallback.nameFr,
          display_order: data.displayOrder || 0,
          quick_notes: data.quickNotes || [],
        }as any)
        .select()
        .single()

      if (error) {
        throw error
      }

      return {
        id: result.id,
        restaurantId: result.restaurant_id,
        nameAr: result.name_ar,
        nameFr: result.name_fr,
        displayOrder: result.display_order || 0,
        quickNotes: (result as any).quick_notes || [],
        createdAt: result.created_at || '',
        updatedAt: result.updated_at || '',
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menuCategories', restaurantId] })
    },
  })
}

/**
 * Update menu category mutation
 * Supports both admin users and assistants with appropriate permissions
 */
export function useUpdateMenuCategory(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      id: string
      nameAr?: string
      nameFr?: string
      displayOrder?: number
      quickNotes?: string[]
    }) => {
      // Apply fallback: if one language is missing, use the other
      const withFallback = applyLanguageFallback({
        nameAr: data.nameAr !== undefined ? data.nameAr || null : undefined,
        nameFr: data.nameFr !== undefined ? data.nameFr || null : undefined,
      } as any)

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {

        // RPC function added by migration 20251211000003
        const { error } = await (supabase.rpc as any)('assistant_update_menu_category', {
          
          p_restaurant_id: restaurantId,
          p_category_id: data.id,
          p_name_ar: withFallback.nameAr || null,
          p_name_fr: withFallback.nameFr || null,
          p_display_order: data.displayOrder !== undefined ? data.displayOrder : null,
          p_quick_notes: data.quickNotes !== undefined ? data.quickNotes : null,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied')
          }
          throw error
        }

        return {
          id: data.id,
          restaurantId,
          nameAr: withFallback.nameAr || '',
          nameFr: withFallback.nameFr || '',
          displayOrder: data.displayOrder || 0,
          quickNotes: data.quickNotes || [],
          createdAt: '',
          updatedAt: '',
        }
      }

      // Admin user - use direct query
      const updateData: any = {}
      if (withFallback.nameAr !== undefined) updateData.name_ar = withFallback.nameAr
      if (withFallback.nameFr !== undefined) updateData.name_fr = withFallback.nameFr
      if (data.displayOrder !== undefined) updateData.display_order = data.displayOrder
      if (data.quickNotes !== undefined) updateData.quick_notes = data.quickNotes

      const { data: result, error } = await supabase
        .from('menu_categories')
        .update(updateData)
        .eq('id', data.id)
        .select()
        .single()

      if (error) {
        throw error
      }

      return {
        id: result.id,
        restaurantId: result.restaurant_id,
        nameAr: result.name_ar,
        nameFr: result.name_fr,
        displayOrder: result.display_order || 0,
        quickNotes: (result as any).quick_notes || [],
        createdAt: result.created_at || '',
        updatedAt: result.updated_at || '',
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menuCategories', restaurantId] })
      queryClient.invalidateQueries({ queryKey: queryKeys.menuItems.list({ restaurantId }) })
    },
  })
}

/**
 * Delete menu category mutation
 * Supports both admin users and assistants with appropriate permissions
 */
export function useDeleteMenuCategory(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (categoryId: string) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {

        // RPC function added by migration 20251211000003
        const { error } = await (supabase.rpc as any)('assistant_delete_menu_category', {
          
          p_restaurant_id: restaurantId,
          p_category_id: categoryId,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied')
          }
          throw error
        }
        return
      }

      // Admin user - use direct query
      const { error } = await supabase.from('menu_categories').delete().eq('id', categoryId)

      if (error) {
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menuCategories', restaurantId] })
      queryClient.invalidateQueries({ queryKey: queryKeys.menuItems.list({ restaurantId }) })
    },
  })
}

